/**
 * Verify state mappings — the public (consumer-facing) representation of
 * a scan result. Randy's Phase 2A uses these exact strings as the
 * `result` field on the verify endpoint response. Admin UI maps its
 * own "active/suspended/left" statuses to these public strings in
 * various places.
 *
 * One source of truth — changing the copy here changes it everywhere.
 */

export const VERIFY_STATES = {
  verified: {
    key:    'verified',
    label:  'Verified',
    tagline: 'Currently employed and in good standing.',
    // Tailored color lexicon — maps to dashboard.css variables
    // but we expose them as tokens for inline styling on the verify page
    // where the design is slightly different from the admin dashboard.
    color:      'var(--tocyn-green-d)',
    background: 'var(--tocyn-green-l)',
    border:     'rgba(0, 171, 57, 0.3)',
    tone:       'positive',
  },
  under_review: {
    key:    'under_review',
    label:  'Under Review',
    tagline: "This staff member's access is temporarily on hold. Contact the organisation to verify.",
    color:      '#92400E',
    background: '#FEF3C7',
    border:     'rgba(180, 83, 9, 0.3)',
    tone:       'caution',
  },
  no_longer_employed: {
    key:    'no_longer_employed',
    label:  'No Longer Employed',
    tagline: "This person no longer works for the organisation.",
    color:      'var(--id-red-d)',
    background: 'var(--id-red-l)',
    border:     'rgba(230, 57, 70, 0.3)',
    tone:       'negative',
  },
  unknown: {
    key:    'unknown',
    label:  'Unknown',
    tagline: "This code doesn't match any active staff member.",
    color:      'var(--ink-mute)',
    background: 'var(--cream)',
    border:     'var(--line)',
    tone:       'neutral',
  },
};

/**
 * Look up a state by its key, with a fallback to 'unknown' for any
 * value the server returns that we don't recognise (forward-compat).
 */
export function getVerifyState(key) {
  return VERIFY_STATES[key] || VERIFY_STATES.unknown;
}

/**
 * Map the admin-side status to the public verify state it'll produce.
 * Used by the staff editor + status pill admin hints.
 *
 *   active    → verified
 *   suspended → under_review
 *   left      → no_longer_employed
 */
export function adminStatusToVerifyState(adminStatus) {
  switch (adminStatus) {
    case 'active':    return VERIFY_STATES.verified;
    case 'suspended': return VERIFY_STATES.under_review;
    case 'left':      return VERIFY_STATES.no_longer_employed;
    default:          return VERIFY_STATES.unknown;
  }
}
